import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  HomeModernIcon, UserGroupIcon, CheckCircleIcon, ClockIcon, BanknotesIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { createPortalStaffPages } from './PortalStaffPortal';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="ct-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="ct-row">
          <div className="ct-dot" style={{ background: p.color }} />
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

const STATUS_LABEL = { active: 'Active', maintenance: 'Maintenance', inactive: 'Inactive' };
const STATUS_COLORS = { active: '#10c98e', maintenance: '#f59e0b', inactive: '#ef4444' };

// Rendered inside the Hostel Member Dashboard (via PortalStaffPortal's
// DashboardExtra slot) — the same overview stats Admin/Finance see in
// HostelOverview.jsx, plus two chart panels, so the dashboard isn't just quick links.
const HostelDashboardStats = ({ apiBase }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`${apiBase}/dashboard-stats`).then(r => setData(r.data)).catch(()=>{}); }, [apiBase]);
  if (!data) return null;
  const { stats, statusBreakdown, allocationsTrend } = data;
  const statusChartData = statusBreakdown.map(r => ({ ...r, label: STATUS_LABEL[r.status] || r.status }));

  return (
    <>
      <div className="stat-grid" style={{gridTemplateColumns:'repeat(5,1fr)'}}>
        <div className="stat-card"><div className="s-icon amber"><HomeModernIcon/></div><div className="s-label">Rooms / Capacity</div><div className="s-value">{stats.totalRooms} / {stats.totalCapacity}</div></div>
        <div className="stat-card"><div className="s-icon blue"><UserGroupIcon/></div><div className="s-label">Occupied</div><div className="s-value">{stats.occupied}</div></div>
        <div className="stat-card"><div className="s-icon green"><CheckCircleIcon/></div><div className="s-label">Vacant</div><div className="s-value">{stats.vacant}</div></div>
        <div className="stat-card"><div className="s-icon amber"><ClockIcon/></div><div className="s-label">Pending Fines</div><div className="s-value">{stats.pendingFines}</div></div>
        <div className="stat-card"><div className="s-icon green"><BanknotesIcon/></div><div className="s-label">Fines Total</div><div className="s-value" style={{fontSize:'1.1rem'}}>Rs. {Number(stats.finesTotal).toLocaleString()}</div></div>
      </div>
      <div className="charts-row">
        <div className="chart-panel">
          <div className="chart-title">Rooms by Status</div>
          <div className="chart-sub">Active, under maintenance, or inactive</div>
          {statusChartData.length === 0 ? <div className="chart-empty">No rooms yet.</div> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={statusChartData} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false} />
                <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Rooms" radius={[4,4,0,0]}>
                  {statusChartData.map((r, i) => <Cell key={i} fill={STATUS_COLORS[r.status] || '#9094a8'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="chart-panel">
          <div className="chart-title">Check-Ins — Last 7 Days</div>
          <div className="chart-sub">Students allocated a room per day</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={allocationsTrend} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false} />
              <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={28} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Check-Ins" fill="#ec4899" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

const pages = createPortalStaffPages({
  apiBase: '/hostel_member',
  roleLabel: 'Hostel',
  accent: '#ec4899',
  DashboardExtra: HostelDashboardStats,
  quickLinks: [
    { label: 'Rooms',         href: '/hostel/rooms' },
    { label: 'Allocation',    href: '/hostel/allocation' },
    { label: 'Fines',         href: '/hostel/fines' },
    { label: 'My Attendance', href: '/hostel/own-attendance' },
    { label: 'My Leaves',     href: '/hostel/own-leaves' },
    { label: 'My Salary',     href: '/hostel/salary' },
  ],
});

export const HostelDashboard    = pages.Dashboard;
export const HostelProfile      = pages.Profile;
export const HostelMyAttendance = pages.MyAttendance;
export const HostelMyLeaves     = pages.MyLeaves;
export const HostelMySalary     = pages.MySalary;
